// Arbeitgeber zuordnen — die Bestandsnamen, die nur als Text am Kontakt
// stehen, echten Firmen zuweisen.
//
// Warum es diese Seite gibt: Der Arbeitgeber wird seit 08/2026 als
// Firmenkontakt verknüpft und nicht mehr getippt (ArbeitgeberFeld). Im Bestand
// steht er bei rund 1.800 Personen nur als Freitext — und ein Name ohne
// Verknüpfung trägt nichts: An der Verbindung hängen betriebliche Vorsorge,
// Organigramm und die Firmenzuordnung der Kommunikation.
//
// Gearbeitet wird je NAME, nicht je Person: „Stadtwerke Aachen" bei vierzig
// Beschäftigten ist ein Vorgang, nicht vierzig.

import { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles, shorthands, Spinner } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Briefcase20Regular,
  Search20Regular,
  Dismiss16Regular,
  Checkmark16Regular,
} from "@fluentui/react-icons";
import { useSmartBack } from "../hooks/smartBack";
import { REFINED } from "../components/risikoanalyseStep/tokens";
import { RelationContactPicker } from "../components/redesign/RelationContactPicker";
import {
  ladeArbeitgeberFreitexte,
  ordneArbeitgeberZu,
  type ArbeitgeberFreitextGruppe,
} from "../services/arbeitgeberFreitextService";

const useStyles = makeStyles({
  root: {
    maxWidth: "1000px",
    ...shorthands.margin("0", "auto"),
    ...shorthands.padding("18px", "20px", "120px"),
  },
  bar: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    marginBottom: "14px",
    ...shorthands.padding("14px", "18px"),
    backgroundImage: `linear-gradient(180deg, ${REFINED.aub50}, ${REFINED.white})`,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("12px"),
  },
  barIcon: { color: REFINED.aub, flexShrink: 0 },
  title: { fontSize: "1.15rem", fontWeight: 700, color: REFINED.ink, margin: 0 },
  subtitle: { fontSize: "0.82rem", color: REFINED.ink2, marginTop: "2px" },
  back: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("7px", "12px"),
    fontSize: "0.82rem",
    color: REFINED.ink2,
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
    cursor: "pointer",
  },
  searchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    marginBottom: "14px",
  },
  searchIcon: { position: "absolute", left: "10px", color: REFINED.ink3, pointerEvents: "none" },
  search: {
    width: "100%",
    ...shorthands.padding("8px", "10px", "8px", "34px"),
    fontSize: "0.85rem",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
  },
  zeile: {
    ...shorthands.padding("13px", "16px"),
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    marginBottom: "9px",
  },
  kopf: { display: "flex", alignItems: "baseline", columnGap: "10px", flexWrap: "wrap" },
  name: { fontSize: "0.98rem", fontWeight: 700, color: REFINED.ink },
  anzahl: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: REFINED.aub,
    backgroundColor: REFINED.aub50,
    ...shorthands.padding("2px", "8px"),
    ...shorthands.borderRadius("999px"),
  },
  personen: { fontSize: "0.79rem", color: REFINED.ink3, marginTop: "4px", lineHeight: 1.5 },
  aktion: { display: "flex", alignItems: "center", columnGap: "10px", marginTop: "10px" },
  btn: {
    ...shorthands.padding("7px", "14px"),
    fontSize: "0.82rem",
    fontWeight: 600,
    color: REFINED.white,
    backgroundColor: REFINED.aub,
    ...shorthands.border("0", "none", "transparent"),
    ...shorthands.borderRadius("8px"),
    cursor: "pointer",
  },
  btnStill: {
    ...shorthands.padding("7px", "14px"),
    fontSize: "0.82rem",
    color: REFINED.ink2,
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
    cursor: "pointer",
  },
  fertig: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "#2c6e52",
  },
  fehler: { fontSize: "0.82rem", color: "#c23b3b", marginTop: "6px" },
  leer: {
    ...shorthands.padding("40px", "20px"),
    textAlign: "center",
    color: REFINED.ink3,
    fontSize: "0.9rem",
  },
});

const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export default function ArbeitgeberCleanupPage() {
  const s = useStyles();
  const goBack = useSmartBack("/datenbereinigung");
  const [gruppen, setGruppen] = useState<ArbeitgeberFreitextGruppe[]>([]);
  const [laden, setLaden] = useState(true);
  const [suche, setSuche] = useState("");
  /** Für welchen Namen ist der Firmen-Picker gerade offen? */
  const [offen, setOffen] = useState<string | null>(null);
  /** Was ist bereits zugeordnet? Name → Anzahl. Bleibt bis zum Neuladen stehen. */
  const [erledigt, setErledigt] = useState<Record<string, number>>({});
  const [fehler, setFehler] = useState<Record<string, string>>({});

  const laden_ = useCallback(() => {
    setLaden(true);
    ladeArbeitgeberFreitexte()
      .then(setGruppen)
      .catch(() => setGruppen([]))
      .finally(() => setLaden(false));
  }, []);

  useEffect(laden_, [laden_]);

  const gefiltert = useMemo(() => {
    const q = norm(suche.trim());
    if (!q) return gruppen;
    return gruppen.filter((g) => norm(g.name).includes(q));
  }, [gruppen, suche]);

  const offeneAnzahl = gruppen.filter((g) => erledigt[g.name] == null).length;
  const personenGesamt = gruppen
    .filter((g) => erledigt[g.name] == null)
    .reduce((n, g) => n + g.anzahl, 0);

  const zuordnen = async (name: string, firmaId: number) => {
    setOffen(null);
    setFehler((f) => ({ ...f, [name]: "" }));
    try {
      const anzahl = await ordneArbeitgeberZu(name, firmaId);
      setErledigt((e) => ({ ...e, [name]: anzahl }));
    } catch {
      setFehler((f) => ({ ...f, [name]: "Zuordnen fehlgeschlagen." }));
    }
  };

  return (
    <div className={s.root}>
      <div className={s.bar}>
        <button type="button" className={s.back} onClick={goBack}>
          <ArrowLeft20Regular /> Zurück
        </button>
        <Briefcase20Regular className={s.barIcon} />
        <div>
          <h1 className={s.title}>Arbeitgeber zuordnen</h1>
          <div className={s.subtitle}>
            {laden
              ? "wird geladen …"
              : `${offeneAnzahl} Firmennamen aus dem Altbestand, ${personenGesamt} Personen betroffen`}
          </div>
        </div>
      </div>

      <div className={s.searchWrap}>
        <Search20Regular className={s.searchIcon} />
        <input
          className={s.search}
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Firmenname suchen …"
        />
      </div>

      {laden && <Spinner label="Arbeitgeber werden geladen …" />}

      {!laden && gefiltert.length === 0 && (
        <div className={s.leer}>
          {suche.trim()
            ? "Kein Firmenname passt zur Suche."
            : "Alle Arbeitgeber sind einer Firma zugeordnet."}
        </div>
      )}

      {!laden &&
        gefiltert.map((g) => (
          <div key={g.name} className={s.zeile}>
            <div className={s.kopf}>
              <span className={s.name}>{g.name}</span>
              <span className={s.anzahl}>
                {g.anzahl} {g.anzahl === 1 ? "Person" : "Personen"}
              </span>
            </div>
            <div className={s.personen}>
              {g.personen.map((p) => p.name).join(" · ")}
              {g.anzahl > g.personen.length && ` … und ${g.anzahl - g.personen.length} weitere`}
            </div>

            {erledigt[g.name] != null ? (
              <div className={s.aktion}>
                <span className={s.fertig}>
                  <Checkmark16Regular />
                  {erledigt[g.name]} {erledigt[g.name] === 1 ? "Person" : "Personen"} zugeordnet
                </span>
              </div>
            ) : offen === g.name ? (
              <div style={{ marginTop: 10 }}>
                {/* Suchen oder neu anlegen — dieselbe Auswahl wie im
                    Arbeitgeber-Feld der Akte. */}
                <RelationContactPicker
                  defaultType="Firma"
                  selected={null}
                  onSelect={(kontakt: any) => {
                    const id = Number(kontakt?.id);
                    if (Number.isFinite(id) && id > 0) void zuordnen(g.name, id);
                  }}
                />
                <button type="button" className={s.btnStill} onClick={() => setOffen(null)}>
                  <Dismiss16Regular /> Abbrechen
                </button>
              </div>
            ) : (
              <div className={s.aktion}>
                <button type="button" className={s.btn} onClick={() => setOffen(g.name)}>
                  Firma zuordnen
                </button>
              </div>
            )}

            {fehler[g.name] && <div className={s.fehler}>{fehler[g.name]}</div>}
          </div>
        ))}
    </div>
  );
}
